import { yupResolver } from "@hookform/resolvers/yup";
import { Form } from "apps/visits/packages/ui/src/components";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type * as yup from "yup";
import { cn } from "@/core/shared/v2/lib/utils";
import { Button } from "@/core/shared/v2/ui/button";
import { Input } from "@/core/shared/v2/ui/input";
import type { AuthorizationMethod } from "../../types/authorization-method";
import { useGtmEvents } from "./use-gtm-events";
import { useValidationSchema } from "./use-validation-schema";

export type FormValues = yup.InferType<ReturnType<typeof useValidationSchema>>;

type UserIdFormProps = {
  onSubmit: SubmitHandler<FormValues>;
  isLoading?: boolean;
  apiError?: string;
  authorizationMethod?: AuthorizationMethod;
  idLabel?: string;
};

export const UserIdForm = ({ onSubmit, apiError, isLoading, authorizationMethod, idLabel }: UserIdFormProps) => {
  const { t } = useTranslation();
  const { handleSubmit, errors, register } = useForm<FormValues>({
    mode: "onBlur",
    resolver: yupResolver(useValidationSchema(t, authorizationMethod)),
  });
  const { handleInputChange } = useGtmEvents(errors, apiError, authorizationMethod);

  const isEmailAuthorization = authorizationMethod === "email_authorization";
  const label = isEmailAuthorization
    ? t("VOUCHERS_DISTRIBUTION.USER_ID_FORM.EMAIL_LABEL")
    : (idLabel ?? t("VOUCHERS_DISTRIBUTION.USER_ID_FORM.ID_NUMBER_LABEL"));
  const errorMessage = errors.userId?.message || apiError;

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <div className="w-full text-left">
        <Input
          id="userId"
          ref={register}
          name="userId"
          placeholder={label}
          aria-invalid={!!errorMessage}
          onChange={handleInputChange}
          showCheckIcon={true}
          data-testid="user-id-input"
        />
        {errorMessage && <p className="mt-2 font-medium text-destructive text-sm">{errorMessage}</p>}
      </div>

      <Button
        type="submit"
        size="lg"
        arrowOnHover={false}
        className={cn("mt-10 w-full", apiError && "mt-6")}
        loading={isLoading}
        data-testid="user-id-submit"
      >
        {t("VOUCHERS_DISTRIBUTION.USER_ID_FORM.SUBMIT")}
      </Button>
    </Form>
  );
};
